import React, { Component } from 'react';

import InputBlock from 'components/input/block/InputBlock';
import SearchFilter from 'components/input/search/SearchFilter';
import { Loader } from 'components/loader';
import { Panel } from 'components/panel';
import { Radiolist } from 'components/radiolist';
import { isObject } from 'helpers/Object';
import { isString, isBlank, normalizeIgnoreCase } from 'helpers/String';
import intersectionBy from 'lodash/intersectionBy';
import isEqual from 'lodash/isEqual';
import unionWith from 'lodash/unionWith';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { ProductService } from 'modules/product';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';

import SurveyProducts from './surveyThirdyProducts';

import { ITEM_TYPE } from '../constants';

@inject('AdvertisementEditorStore', 'UiStore')
@observer
class ProductRadiolist extends Component {
  // #region setup
  static propTypes = {
    AdvertisementEditorStore: MobxPropTypes.objectOrObservableObject,
    labelValue: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onChange: PropTypes.func,
    UiStore: MobxPropTypes.objectOrObservableObject
  };

  state = {
    affiliateProductsPage: 0,
    affiliateProductsSize: 0,
    affiliateProducts: [],
    filteredProducts: [],
    myProducts: [],
    loading: false,
    searchText: '',
    searchResults: [],
    searchResultsPage: 0,
    searchResultsSize: null
  };

  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.onRadiolistChange = this.onRadiolistChange.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.setInitialStateAsync();
  }

  async setInitialStateAsync() {
    await this.getAllMyProducts();
    await this.getAffiliateProducts();

    this.updateFilteredProducts();
  }

  updateProvidedSelectedItem() {
    const { value } = this.props;
    const isHashProduct = value && isString(value);

    if (isHashProduct) {
      this.onProductChange(value);
    }
  }

  resetSearchInfo() {
    this.setState({
      searchResults: [],
      searchResultsPage: 0,
      searchResultsSize: null
    });
  }
  // #endregion

  // #region actions
  async getAllMyProducts(page = 1) {
    let { myProducts } = this.state;
    let response = null;
    const { value: productId } = this.props;

    this.setState({ loading: true });

    try {
      response = await ProductService.getProductsByProducer({
        role: 'producer',
        page
      });

      if (response && response.data && response.data.length) {
        myProducts = myProducts.concat(response.data);

        this.setState({ myProducts }, () => {
          if (myProducts.length !== response.size) {
            page += 1;
            this.getAllMyProducts(page);
          } else if (isString(productId) && !isBlank(productId)) {
            this.checkIfProductExist(productId);
          }
        });
      }
    } catch (error) {
      UiService.setAlertError('default_error');
    }

    this.setState({ loading: false });
    this.updateProvidedSelectedItem();

    return response;
  }

  async getAffiliateProducts(hash) {
    let { affiliateProducts, affiliateProductsPage } = this.state;
    let response = null;

    this.setState({ loading: true });

    try {
      response = await ProductService.getProductsByProducer({
        role: 'affiliate',
        page: ++affiliateProductsPage
      });

      if (response && response.data && response.data.length) {
        affiliateProducts = unionWith(
          affiliateProducts,
          response.data,
          isEqual
        );

        this.setState(
          {
            affiliateProducts,
            affiliateProductsPage: response.page,
            affiliateProductsSize: response.size
          },
          () => {
            hash && this.checkIfProductExist(hash);
          }
        );
      }
    } catch (error) {
      UiService.setAlertError('default_error');
    }

    this.setState({ loading: false });
    this.updateProvidedSelectedItem();

    return response;
  }

  async checkIfProductExist(hash) {
    const { value } = this.props;
    const product = this.getProductByHash(hash);

    if (!product) {
      await this.getAffiliateProducts(hash);
    } else if (!isEqual(product, value)) {
      this.setProduct(product);
    }
  }

  async searchProducts() {
    const { searchText } = this.state;
    let { searchResults, searchResultsPage, affiliateProducts } = this.state;
    let response = null;

    this.setState({ loading: true });

    try {
      response = await ProductService.getProductsByProducer({
        role: 'affiliate',
        page: ++searchResultsPage,
        name: searchText || null
      });

      if (response && response.data) {
        searchResults = response.data;
        affiliateProducts = unionWith(
          affiliateProducts,
          response.data,
          isEqual
        );

        this.setState({
          searchResults,
          affiliateProducts,
          searchResultsPage: response.page,
          searchResultsSize: response.size
        });
      }
    } catch (error) {
      UiService.setAlertError('default_error');
    }

    this.setState({ loading: false });
    this.updateProvidedSelectedItem();

    return response;
  }
  // #endregion

  // #region getters
  get shouldFetchAffiliateProducts() {
    const { affiliateProducts, affiliateProductsSize } = this.state;

    return affiliateProducts.length < affiliateProductsSize;
  }

  get shouldFetchAffiliateProductsBySearch() {
    const { searchResults, searchResultsSize } = this.state;

    return (
      this.shouldFetchAffiliateProducts &&
      searchResults.length !== searchResultsSize
    );
  }

  get lookForSearchSource() {
    return Boolean(this.state.searchText && this.shouldFetchAffiliateProducts);
  }

  shouldRender = (item, searchText) =>
    this.isHeaderItem(item) || this.containsSearchText(item, searchText);

  get products() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const { searchText, searchResults, myProducts } = this.state;
    const { affiliateProducts } = !this.lookForSearchSource
      ? this.state
      : { affiliateProducts: searchResults };

    const myProductsFiltered = myProducts.filter((item) =>
      that.shouldRender(item, searchText)
    );

    myProductsFiltered.unshift(
      ...this.heading('my_products', myProductsFiltered.length)
    );

    const affiliateProductsFiltered = affiliateProducts.filter((item) =>
      that.shouldRender(item, searchText)
    );

    affiliateProductsFiltered.unshift(
      ...this.heading('affiliate_products', affiliateProductsFiltered.length)
    );

    const products = [...myProductsFiltered, ...affiliateProductsFiltered];

    return products.map((product) => {
      product.value = product.id;
      return product;
    });
  }

  get selectedOption() {
    const { value } = this.props;
    const selected = isObject(value) ? value.id : null;

    return selected;
  }

  getProductByHash(id) {
    return intersectionBy(this.products, [{ id }], 'id')[0];
  }
  // #endregion

  // #region helpers
  isHeaderItem = (item) =>
    [ITEM_TYPE.HEADER, ITEM_TYPE.SUBHEADER].includes(item.type);
  containsSearchText = (item, searchText) =>
    normalizeIgnoreCase(item.name).includes(normalizeIgnoreCase(searchText));
  headingFilter = (item) =>
    ![ITEM_TYPE.HEADER, ITEM_TYPE.SUBHEADER].includes(item.type);

  heading(category, length) {
    if (!length) {
      return [];
    }

    const { isMobile } = this.props.UiStore;
    const heading = [
      { type: ITEM_TYPE.HEADER, name: `advertisement_editor.${category}` }
    ];

    return isMobile
      ? heading
      : heading.concat([{ type: ITEM_TYPE.SUBHEADER, name: 'general.name' }]);
  }
  // #endregion

  // #region handlers
  async handleScroll(event) {
    const {
      scrollHeight,
      clientHeight,
      scrollTop: scrollPosition
    } = event.nativeEvent.target;
    const maxScroll = scrollHeight - clientHeight;

    if (maxScroll === scrollPosition) {
      const { searchText } = this.state;

      if (searchText && this.shouldFetchAffiliateProductsBySearch) {
        this.setSearchText(searchText, async () => await this.searchProducts());
      } else if (this.shouldFetchAffiliateProducts) {
        await this.getAffiliateProducts();
      }

      this.updateFilteredProducts();
    }
  }

  async handleChange(searchText) {
    await this.resetSearchInfo();

    this.setSearchText(searchText, async () => {
      this.shouldFetchAffiliateProductsBySearch &&
        (await this.searchProducts());
      this.updateFilteredProducts();
    });
  }

  updateFilteredProducts = () =>
    this.setState({ filteredProducts: this.products });

  setSearchText = (searchText = '', callback) =>
    this.setState({ searchText }, () => callback && callback());

  onProductChange(hash) {
    const product = this.getProductByHash(hash);

    if (product) {
      this.setProduct(product);
    } else if (this.shouldFetchAffiliateProducts) {
      this.getAffiliateProducts(hash);
    }
  }

  onRadiolistChange = (event) => {
    const { value } = event.target;
    const product = this.products.find((item) => item.id === value);

    this.setProduct(product);
  };

  setProduct(value) {
    const { name, onChange } = this.props;

    onChange && onChange({ target: { name, value } });
  }
  // #endregion

  render() {
    const { loading, filteredProducts } = this.state;
    const { labelValue, ...props } = this.props;

    return (
      <InputBlock
        {...props}
        labelValue={labelValue}
        className="product__radiolist__input"
      >
        <div className="product__radiolist">
          <Panel
            nopadding
            key="product__radiolist__panel"
            handleScroll={this.handleScroll}
          >
            <SearchFilter items={filteredProducts}>
              <SearchFilter.SearchInput
                maxLength="100"
                autoComplete="off"
                onChange={this.handleChange}
                noborder
              />

              <SurveyProducts />

              <Radiolist
                {...props}
                selectedOption={this.selectedOption}
                onChange={this.onRadiolistChange}
              />
            </SearchFilter>
          </Panel>
          {loading && <Loader key="loader" mask />}
        </div>
      </InputBlock>
    );
  }
}

export default ProductRadiolist;
